import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EventBus } from 'src/common/EventBus';
import { 
  CheckCircleIcon, 
  ChevronUpIcon, 
  ChevronDownIcon, 
  XIcon,
  ArrowRightIcon
} from '@heroicons/react/outline';
import Confetti from './Confetti';
import { OnboardingChecklistService, OnboardingChecklistItem } from './onboardingChecklistService';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  action?: () => void;
  targetSelector?: string;
  url?: string;
  tooltipTarget?: string;
}

interface OnboardingChecklistProps {
  isVisible: boolean;
  onClose: () => void;
  onComplete: () => void;
  organizationSlug?: string;
  isLoading?: boolean;
}

const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  isVisible,
  onClose,
  onComplete,
  organizationSlug,
  isLoading: isLoadingProp
}) => {
  const history = useHistory();
  const location = useLocation();
  
  // Detect if we're in an editor context (microsite or pitch mode)
  const isInEditor = location.pathname.includes('/journeys/edit');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const [tooltipState, setTooltipState] = useState<{
    visible: boolean;
    text: string;
    targetElement: HTMLElement | null;
  }>({
    visible: false,
    text: '',
    targetElement: null
  });
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([
    {
      id: 'customize_branding',
      title: 'Customize Branding',
      description: 'Set up your brand colors and logo',
      completed: false,
      targetSelector: '[data-testid="branding-settings"]',
      url: '/branding'
    },
    {
      id: 'add_media',
      title: 'Add Content to Library',
      description: 'Upload and organize your content assets',
      completed: false,
      targetSelector: '[data-testid="library-content"]',
      url: '/library/content'
    },
    {
      id: 'create_journey',
      title: 'Create Your First Journey',
      description: 'Start building content for your audience',
      completed: false,
      targetSelector: '[data-testid="create_journey"]',
      tooltipTarget: 'create_journey'
    },
    {
      id: 'view_insights',
      title: 'View Insights',
      description: 'Check your journey analytics and performance',
      completed: false,
      targetSelector: '[data-testid="insights-section"]',
      url: '/insights/journeys'
    },
    {
      id: 'invite_team',
      title: 'Invite Team Members',
      description: 'Add colleagues to collaborate on your projects',
      completed: false,
      targetSelector: '[data-testid="team-invite"]',
      url: '/organization/members'
    }
  ]);

  const [backendProgress, setBackendProgress] = useState<OnboardingChecklistItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load backend progress on component mount
  useEffect(() => {
    const loadBackendProgress = async () => {
      if (!organizationSlug) return;
      
      try {
        setIsLoading(true);
        const progress = await OnboardingChecklistService.getProgress(organizationSlug);
        setBackendProgress(progress.progress);
        
        // Sync backend progress with local state
        setChecklistItems(prevItems =>
          prevItems.map(item => {
            const backendItem = progress.progress.find(bi => bi.item_type === item.id);
            return backendItem ? { ...item, completed: backendItem.is_completed } : item;
          })
        );
      } catch (error) {
        console.error('Failed to load backend progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBackendProgress();
  }, [organizationSlug]);

  // EventBus listeners for automatic checklist completion
  useEffect(() => {
    // Listen for branding customization events
    const handleBrandingCustomized = async () => {
      // Update local state
      setChecklistItems(prevItems =>
        prevItems.map(item =>
          item.id === 'customize_branding' ? { ...item, completed: true } : item
        )
      );
      
      // Update backend
      if (organizationSlug) {
        try {
          await OnboardingChecklistService.markItemCompletedFromEvent(
            organizationSlug, 
            'onboarding:branding-customized'
          );
        } catch (error) {
          console.error('Failed to update backend for branding customization:', error);
        }
      }
    };

    // Listen for journey creation events
    const handleJourneyCreated = async () => {
      // Update local state
      setChecklistItems(prevItems =>
        prevItems.map(item =>
          item.id === 'create_journey' ? { ...item, completed: true } : item
        )
      );
      
      // Show confetti and celebration for first journey creation
      setShowConfetti(true);
      setShowCelebration(true);
      
      // Hide celebration message after 5 seconds
      setTimeout(() => {
        setShowCelebration(false);
      }, 5000);
      
      // Update backend
      if (organizationSlug) {
        try {
          await OnboardingChecklistService.markItemCompletedFromEvent(
            organizationSlug, 
            'onboarding:journey-created'
          );
        } catch (error) {
          console.error('Failed to update backend for journey creation:', error);
        }
      }
    };

    // Listen for content library addition events
    const handleContentAddedToLibrary = async () => {
      // Update local state
      setChecklistItems(prevItems =>
        prevItems.map(item =>
          item.id === 'add_media' ? { ...item, completed: true } : item
        )
      );
      
      // Update backend
      if (organizationSlug) {
        try {
          await OnboardingChecklistService.markItemCompletedFromEvent(
            organizationSlug, 
            'onboarding:media-added'
          );
        } catch (error) {
          console.error('Failed to update backend for content library addition:', error);
        }
      }
    };

    // Listen for insights viewing events
    const handleInsightsViewed = async () => {
      // Update local state
      setChecklistItems(prevItems =>
        prevItems.map(item =>
          item.id === 'view_insights' ? { ...item, completed: true } : item
        )
      );
      
      // Update backend
      if (organizationSlug) {
        try {
          await OnboardingChecklistService.markItemCompletedFromEvent(
            organizationSlug, 
            'onboarding:insights-viewed'
          );
        } catch (error) {
          console.error('Failed to update backend for insights viewed:', error);
        }
      }
    };

    // Listen for team invitation events
    const handleTeamInvited = async () => {
      // Update local state
      setChecklistItems(prevItems =>
        prevItems.map(item =>
          item.id === 'invite_team' ? { ...item, completed: true } : item
        )
      );
      
      // Update backend
      if (organizationSlug) {
        try {
          await OnboardingChecklistService.markItemCompletedFromEvent(
            organizationSlug, 
            'onboarding:team-invited'
          );
        } catch (error) {
          console.error('Failed to update backend for team invitation:', error);
        }
      }
    };

    // Register event listeners
    EventBus.on('onboarding:branding-customized', handleBrandingCustomized);
    EventBus.on('onboarding:journey-created', handleJourneyCreated);
    EventBus.on('onboarding:media-added', handleContentAddedToLibrary);
    EventBus.on('onboarding:insights-viewed', handleInsightsViewed);
    EventBus.on('onboarding:team-invited', handleTeamInvited);

    // Cleanup event listeners
    return () => {
      EventBus.off('onboarding:branding-customized', handleBrandingCustomized);
      EventBus.off('onboarding:journey-created', handleJourneyCreated);
      EventBus.off('onboarding:media-added', handleContentAddedToLibrary);
      EventBus.off('onboarding:insights-viewed', handleInsightsViewed);
      EventBus.off('onboarding:team-invited', handleTeamInvited);
    };
  }, []);

  // Check if all items are completed
  useEffect(() => {
    const allCompleted = checklistItems.every(item => item.completed);
    if (allCompleted) {
      onComplete();
    }
  }, [checklistItems, onComplete]);

  const handleTaskClick = (item: ChecklistItem) => {
    if (item.url && organizationSlug) {
      // Navigate to the specified URL with organization slug
      const fullUrl = `/${organizationSlug}${item.url}`;
      history.push(fullUrl);
    } else if (item.tooltipTarget) {
      // Show tooltip on the specified target element
      let targetElement: HTMLElement | null = null;
      
      // Try multiple selectors for create journey button
      if (item.tooltipTarget === 'create_journey') {
        const selectors = [
          '[data-testid="create_journey"]',
          '.create_journey-button',
          '.btn-create_journey',
          '[href*="journey/new"]',
          '[href*="journeys/new"]'
        ];
        
        for (const selector of selectors) {
          try {
            const element = document.querySelector(selector);
            if (element) {
              targetElement = element as HTMLElement;
              break;
            }
          } catch (e) {
            // Continue to next selector
          }
        }
        
        // If no element found by selector, search for text content
        if (!targetElement) {
          const buttons = document.querySelectorAll('button, a');
          for (const button of buttons) {
            if (button.textContent?.toLowerCase().includes('create journey')) {
              targetElement = button as HTMLElement;
              break;
            }
          }
        }
      }
      
      if (targetElement) {
        // Scroll to the element and highlight it
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a temporary highlight effect
        targetElement.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50');
        
        // Show tooltip
        setTooltipState({
          visible: true,
          text: 'You can create a journey by clicking here',
          targetElement: targetElement
        });
        
        // Store reference to element for timeout callback
        const elementToHighlight = targetElement;
        setTimeout(() => {
          if (elementToHighlight && elementToHighlight.classList) {
            elementToHighlight.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50');
          }
          // Hide tooltip after highlighting
          setTooltipState(prev => ({ ...prev, visible: false }));
        }, 3000);
      } else {
        // If target element not found, show a helpful message
        alert(`Please look for the "Create Journey" button on this page to create your first journey.`);
      }
    }
  };

  const toggleItem = async (itemId: string) => {
    // Update local state
    setChecklistItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, completed: !item.completed } : item
      )
    );
    
    // Update backend
    if (organizationSlug) {
      try {
        const currentItem = checklistItems.find(item => item.id === itemId);
        if (currentItem) {
          await OnboardingChecklistService.toggleItem(
            organizationSlug,
            itemId,
            !currentItem.completed
          );
        }
      } catch (error) {
        console.error('Failed to update backend for item toggle:', error);
        // Revert local state if backend update fails
        setChecklistItems(prevItems =>
          prevItems.map(item =>
            item.id === itemId ? { ...item, completed: !item.completed } : item
          )
        );
      }
    }
  };

  const completedCount = checklistItems.filter(item => item.completed).length;
  const totalCount = checklistItems.length;

  
  if (!isVisible) {
    return null;
  }

  return (
    <>
      <Confetti 
        isVisible={showConfetti} 
        onComplete={() => setShowConfetti(false)} 
      />
      <div className={`fixed z-[99999] max-w-sm ${
        isInEditor 
          ? 'left-6 bottom-16' // Adjust position for editor context
          : 'left-6 bottom-6'  // Default position
      }`}>
        {/* Celebration Message */}
        {showCelebration && (
          <div className="mb-4 bg-gradient-to-r from-green-500 to-blue-500 text-white p-4 rounded-lg shadow-lg animate-bounce">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🎉</span>
              <div>
                <h4 className="font-semibold text-sm">Congratulations!</h4>
                <p className="text-xs">You've created your first journey! Keep going!</p>
              </div>
            </div>
          </div>
        )}
        
        <div className={`bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden ${
          isInEditor 
            ? 'shadow-2xl' // Slightly more prominent shadow in editor
            : ''
        }`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
            {isLoadingProp && (
              <div className="flex items-center justify-center mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="ml-2 text-xs">Syncing with backend...</span>
              </div>
            )}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Getting Started</h3>
                <p className="text-xs text-blue-100">
                  {completedCount} of {totalCount} completed
                  {isInEditor && <span className="ml-2 px-2 py-0.5 bg-white bg-opacity-20 rounded text-xs">Editor Mode</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
              >
                {isCollapsed ? (
                  <ChevronDownIcon className="w-4 h-4" />
                ) : (
                  <ChevronUpIcon className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-3">
            <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
              <div
                className="bg-white h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Checklist items */}
        {!isCollapsed && (
          <div className="max-h-96 overflow-y-auto">
            {checklistItems.map((item) => (
              <div
                key={item.id}
                className={`p-4 border-b border-gray-100 last:border-b-0 transition-all duration-200 ${
                  item.completed ? 'bg-green-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <button
                    onClick={() => toggleItem(item.id)}
                    className={`mt-0.5 flex-shrink-0 transition-all duration-200 ${
                      item.completed
                        ? 'text-green-600 hover:text-green-700'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {item.completed ? (
                      <CheckCircleIcon className="w-5 h-5" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-400 rounded-full" />
                    )}
                  </button>
                  <button
                    onClick={() => handleTaskClick(item)}
                    className={`flex-1 min-w-0 text-left transition-all duration-200 ${
                      item.completed
                        ? 'opacity-60 cursor-default'
                        : 'hover:bg-gray-50 cursor-pointer'
                    }`}
                    disabled={item.completed}
                  >
                    <h4
                      className={`text-sm font-medium transition-all duration-200 ${
                        item.completed
                          ? 'text-green-800 line-through'
                          : 'text-gray-900'
                      }`}
                    >
                      {item.title}
                    </h4>
                    <p
                      className={`text-xs mt-1 transition-all duration-200 ${
                        item.completed
                          ? 'text-green-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {item.description}
                    </p>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {!isCollapsed && (
          <div className="bg-gray-50 p-3 text-center">
            <p className="text-xs text-gray-500">
              {completedCount === totalCount
                ? "🎉 You're all set! Welcome to Journey!"
                : "Complete these steps to get the most out of Journey"}
            </p>
          </div>
        )}
      </div>
      
      {/* Tooltip */}
      {tooltipState.visible && tooltipState.targetElement && (
        <div
          className="fixed z-[60] bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg max-w-xs"
          style={{
            top: tooltipState.targetElement.getBoundingClientRect().top - 50,
            left: tooltipState.targetElement.getBoundingClientRect().left + (tooltipState.targetElement.offsetWidth / 2) - 100
          }}
        >
          {tooltipState.text}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
    </>
  );
};

export default OnboardingChecklist;
